"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { X } from "lucide-react";
import { emitChatTeaserActive, loadChatwootSdk, openChatwootOrFallback } from "@/lib/chatwoot";
import { useChatwootConfig } from "@/hooks/useChatwootConfig";
import { track } from "@/lib/analytics/mixpanel";

// Rotating pool of proactive nudges — picked at random (not in sequence) each
// time a new message is due, so returning visitors don't see the same fixed
// script. Short and curiosity-driven rather than salesy, so it reads like a
// real person popping in, not a bot.
const MESSAGES = [
  "Poking around the infra? I can save you a few tabs.",
  "Not sure if you need shared, dedicated, or your own validator? Tell me what you're building.",
  "Curious how fast you can get a node live? Ask me anything.",
  "Got a question about pricing or uptime? I'm right here.",
];

// Once-per-tab so returning visitors within the same session aren't nagged
// on every navigation back to the homepage. Dismissing (✕) or opening chat
// both set this, so the bubble stays gone for the rest of the session either way.
const SESSION_KEY = "instanodes_chat_teaser_shown";

const INITIAL_DELAY_RANGE: [number, number] = [3000, 5000];
const ROTATE_DELAY_RANGE: [number, number] = [7000, 10000];

// Timing jitter and message ordering for a proactive UI nudge, not a security
// decision — Math.random() is intentional here. NOSONAR
function randomDelay([min, max]: [number, number]) {
  return min + Math.random() * (max - min); // NOSONAR
}

function pickRandomMessageIndex(excludeIndex: number) {
  if (MESSAGES.length <= 1) return 0;
  let index = Math.floor(Math.random() * MESSAGES.length); // NOSONAR
  while (index === excludeIndex)
    index = Math.floor(Math.random() * MESSAGES.length); // NOSONAR
  return index;
}

export default function ChatwootTeaser() {
  // Same gate ChatwootLauncher uses — no point pointing a message at an icon
  // that isn't rendered (missing chat config on e.g. a preview deploy).
  const config = useChatwootConfig();
  const [visible, setVisible] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);

  // Tells ChatwootLauncher (rendered in the root layout, outside this
  // component's subtree) to switch into its attention animation while a
  // message is showing here, and back to idle once it isn't. Split from the
  // timer effect below so a visibility change doesn't also re-run an unmount
  // cleanup — that would emit a spurious false→true blip on every transition.
  useEffect(() => {
    emitChatTeaserActive(visible);
  }, [visible]);

  useEffect(() => {
    return () => emitChatTeaserActive(false);
  }, []);

  useEffect(() => {
    if (!config) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const showTimer = window.setTimeout(() => {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      const index = pickRandomMessageIndex(-1);
      setMessageIndex(index);
      setVisible(true);
      track("Chat Teaser Shown", { message: MESSAGES[index] });
    }, randomDelay(INITIAL_DELAY_RANGE));

    return () => window.clearTimeout(showTimer);
  }, [config]);

  // Re-arms every time messageIndex changes, so each message gets its own
  // fresh 4-5s window rather than rotating on a fixed global clock.
  useEffect(() => {
    if (!visible) return;

    const rotateTimer = window.setTimeout(() => {
      const nextIndex = pickRandomMessageIndex(messageIndex);
      setMessageIndex(nextIndex);
      track("Chat Teaser Shown", { message: MESSAGES[nextIndex] });
    }, randomDelay(ROTATE_DELAY_RANGE));

    return () => window.clearTimeout(rotateTimer);
  }, [visible, messageIndex]);

  function dismiss(reason: "closed" | "opened") {
    setVisible(false);
    sessionStorage.setItem(SESSION_KEY, "1");
    track(
      reason === "opened" ? "Chat Teaser Clicked" : "Chat Teaser Dismissed",
      { message: MESSAGES[messageIndex] },
    );
  }

  // Hovering/focusing the message itself is as strong an intent signal as
  // the launcher icon, so preload here too — idempotent, so it's free to
  // call more than once.
  function handlePreload() {
    if (config) loadChatwootSdk(config);
  }

  function handleOpen() {
    if (config) openChatwootOrFallback(config);
    dismiss("opened");
  }

  // After all hooks — same rendering gate as ChatwootLauncher, so a message
  // never shows up pointing at an icon that isn't there.
  if (!config) return null;

  return (
    <div className="fixed bottom-24 right-6 z-[9997] w-64">
      {/* mode="wait" makes message swaps sequential: the outgoing bubble
          sinks away before the next one pops in, instead of cross-fading. */}
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div
            key={messageIndex}
            initial={{ opacity: 0, scale: 0.4, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.55, y: 18 }}
            transition={{ type: "spring", stiffness: 380, damping: 18 }}
            role="status"
            aria-live="polite"
            className="relative flex origin-bottom-right items-start gap-3 rounded-2xl border border-brand-purple/30 bg-bg-card px-4 py-3.5 pr-8 shadow-xl"
          >
            <button
              type="button"
              onClick={handleOpen}
              onMouseEnter={handlePreload}
              onFocus={handlePreload}
              onTouchStart={handlePreload}
              className="text-left text-sm leading-relaxed text-text-primary"
            >
              {MESSAGES[messageIndex]}
            </button>

            <button
              type="button"
              onClick={() => dismiss("closed")}
              aria-label="Dismiss"
              className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-text-muted transition-colors hover:text-text-primary"
            >
              <X size={13} aria-hidden />
            </button>

            <span
              aria-hidden
              className="absolute -bottom-1.5 right-9 h-3 w-3 rotate-45 rounded-[1px] border-b border-r border-brand-purple/30 bg-bg-card"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
